import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@services/api";
import { useAppDispatch } from "@store/index";
import { setChatOpen, setXlsxContext } from "@features/ui/uiSlice";
import type { XlsxAnchor } from "../../../types/xlsxAnchor";
import { Button } from "@/components/ui/button";

interface Cell {
  ref: string;
  text: string;
  hidden: boolean;
  missingCache: boolean;
  truncated: boolean;
}
interface Grid {
  documentId: string;
  sha256: string;
  sheet: string;
  sheets: string[];
  range: string;
  rows: Cell[][];
  maxRow: number;
  maxColumn: number;
  merges: string[];
}
const point = (ref: string) => {
  const match = /^([A-Z]+)([1-9][0-9]*)$/.exec(ref);
  if (!match) return null;
  return {
    col: [...match[1]].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0),
    row: Number(match[2]),
  };
};
const column = (n: number): string =>
  n > 26
    ? column(Math.floor((n - 1) / 26)) +
      String.fromCharCode(65 + ((n - 1) % 26))
    : String.fromCharCode(64 + n);
const contains = (ref: string, range: string) => {
  const [a, b = a] = range.split(":").map(point);
  const c = point(ref);
  return !!(
    a &&
    b &&
    c &&
    c.row >= a.row &&
    c.row <= b.row &&
    c.col >= a.col &&
    c.col <= b.col
  );
};

export default function XlsxCellViewer({
  documentId,
  anchor,
}: {
  documentId: string;
  anchor?: XlsxAnchor;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const start = point(anchor?.range.split(":")[0] || "A1")!;
  const [sheet, setSheet] = useState(anchor?.sheet || "");
  const [row, setRow] = useState(start.row);
  const [col, setCol] = useState(start.col);
  const [grid, setGrid] = useState<Grid | null>(null);
  const [range, setRange] = useState(anchor?.range || "");
  const [first, setFirst] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const expectedHash = anchor?.sha256;
  useEffect(() => {
    let active = true;
    setLoading(true);
    setGrid(null);
    setError("");
    const params: Record<string, string> = {
      range: `${column(col)}${row}:${column(Math.min(col + 19, 1024))}${Math.min(row + 99, 20000)}`,
    };
    if (sheet) params.sheet = sheet;
    if (expectedHash) params.sha256 = expectedHash;
    api
      .get<Grid>(`/documents/${documentId}/cells`, { params })
      .then((data) => {
        if (!active) return;
        setGrid(data);
      })
      .catch((err) => {
        if (active) setError(err?.code === "HTTP_409" ? "xlsx_source_changed" : "unavailable");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [documentId, sheet, row, col, expectedHash, revision]);
  const selected =
    grid?.rows.flat().filter((cell) => contains(cell.ref, range)) || [];
  const [a, b = a] = range.split(":").map(point);
  const count = a && b ? (b.row - a.row + 1) * (b.col - a.col + 1) : 0;
  const valid =
    !loading &&
    !error &&
    count > 0 &&
    count <= 400 &&
    selected.length === count &&
    selected.every((c) => !c.hidden);
  const select = (ref: string, extend: boolean) => {
    if (!extend || !first) {
      setFirst(ref);
      setRange(ref);
      return;
    }
    const a = point(first)!;
    const b = point(ref)!;
    setRange(
      `${column(Math.min(a.col, b.col))}${Math.min(a.row, b.row)}:${column(Math.max(a.col, b.col))}${Math.max(a.row, b.row)}`,
    );
  };
  const move = (nextRow: number, nextCol: number) => {
    setRange("");
    setFirst("");
    setRow(nextRow);
    setCol(nextCol);
  };
  return (
    <section className="space-y-3" aria-label={t("documents.cells.title")}>
      <p className="text-sm text-muted-foreground">
        {t("documents.cells.notice")}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label>
          {t("documents.cells.sheet")}{" "}
          <select
            aria-label={t("documents.cells.sheet")}
            value={grid?.sheet || sheet}
            disabled={loading || !!error}
            onChange={(e) => {
              setSheet(e.target.value);
              move(1, 1);
            }}
          >
            {(grid?.sheets || [sheet]).map((name) => (
              <option key={name}>{name}</option>
            ))}
          </select>
        </label>
        <span>
          {grid?.sheet} {range}
        </span>
        <Button
          disabled={!valid}
          onClick={() => {
            if (!grid) return;
            dispatch(
              setXlsxContext({
                documentId,
                sha256: grid.sha256,
                sheet: grid.sheet,
                range,
              }),
            );
            dispatch(setChatOpen(true));
          }}
        >
          {t("documents.cells.ask")}
        </Button>
      </div>
      {loading && <p role="status">{t("documents.cells.loading")}</p>}
      {error && (
        <div role="alert">
          <p>
            {t(
              error === "xlsx_source_changed"
                ? "documents.cells.stale"
                : "documents.cells.failed",
            )}
          </p>
          <Button onClick={() => setRevision((v) => v + 1)}>
            {t("common.retry")}
          </Button>
        </div>
      )}
      {grid && (
        <>
          <div className="max-h-[520px] overflow-auto rounded border">
            <table
              className="border-collapse text-sm"
              aria-label={t("documents.cells.grid")}
            >
              <tbody>
                {grid.rows.map((cells, index) => (
                  <tr key={index}>
                    <th className="sticky left-0 border bg-muted px-2">
                      {row + index}
                    </th>
                    {cells.map((cell) => (
                      <td key={cell.ref} className="border p-0">
                        <button
                          type="button"
                          aria-label={`${cell.ref}${cell.hidden ? "" : ` ${cell.text}`}`}
                          aria-pressed={contains(cell.ref, range)}
                          disabled={cell.hidden}
                          onClick={(e) => select(cell.ref, e.shiftKey)}
                          className={`w-full min-h-10 min-w-28 max-w-64 whitespace-pre-wrap break-words p-2 text-left ${contains(cell.ref, range) && !cell.hidden ? "bg-blue-100 text-blue-950 ring-2 ring-inset ring-blue-500" : ""}`}
                        >
                          <span className="block text-[10px] text-muted-foreground">
                            {cell.ref}
                          </span>
                          {cell.hidden
                            ? t("documents.cells.hidden")
                            : cell.missingCache
                              ? t("documents.cells.noCache")
                              : cell.text}
                          {cell.truncated && "…"}
                        </button>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {grid.merges.length > 0 && (
            <p className="text-xs">
              {t("documents.cells.merges")}: {grid.merges.join(", ")}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={row === 1}
              onClick={() => move(Math.max(1, row - 100), col)}
            >
              {t("documents.cells.previousRows")}
            </Button>
            <Button
              disabled={row + 100 > grid.maxRow}
              onClick={() => move(row + 100, col)}
            >
              {t("documents.cells.nextRows")}
            </Button>
            <Button
              disabled={col === 1}
              onClick={() => move(row, Math.max(1, col - 20))}
            >
              {t("documents.cells.previousColumns")}
            </Button>
            <Button
              disabled={col + 20 > grid.maxColumn}
              onClick={() => move(row, col + 20)}
            >
              {t("documents.cells.nextColumns")}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
